/**
 * Pages, one under another. §6.2.
 *
 * Canva's arrangement: every page on screen at once, stacked and scrolled
 * through, each with its own title and actions above it. A two-sided card is
 * the front with the back underneath, which is how a customer thinks of it.
 *
 * One page is the active one - the one being edited - and the view's origin
 * (view.x, view.y) is always the top-left of that page's trim. Everything that
 * works on a page (dragging, snapping, the pen, cropping, rulers) was written
 * against that origin and keeps working unchanged; the other pages are simply
 * drawn at their distance from it. Changing the active page moves the origin
 * by exactly that distance, so the page clicked on stays where it is on screen.
 */

import type { TrimboxDocument } from '../model/types';
import { mmToPx } from '../model/units';

/** Room above each page for its title and actions, in screen pixels. */
export const PAGE_HEAD = 44;

/** Space between one page and the next page's title. */
export const PAGE_GAP = 28;

/** Above the active page when it is fitted: the rulers, the floating toolbar, and its title. */
export const FIT_TOP = 20 + 60 + PAGE_HEAD;

/** Below it: the bottom bar and a little air. */
export const FIT_BOTTOM = 48 + 28;

/**
 * Where a page's trim sits, relative to the first page's trim, in screen
 * pixels at this zoom. Pages are centred on one another, so a narrower page
 * sits in the middle of a wider one's column.
 */
export function pageOffset( doc: TrimboxDocument, index: number, zoom: number ): { x: number; y: number } {
  const first = doc.pages[ 0 ];
  const page = doc.pages[ index ];

  if ( ! first || ! page ) return { x: 0, y: 0 };

  let y = 0;

  for ( let i = 0; i < index; i++ ) {
    const before = doc.pages[ i ];

    if ( before ) y += mmToPx( before.size.h + doc.bleed * 2, zoom ) + PAGE_GAP + PAGE_HEAD;
  }

  return { x: mmToPx( ( first.size.w - page.size.w ) / 2, zoom ), y };
}

/** A page's position relative to the active one - which is what the canvas draws from. */
export function fromActive( doc: TrimboxDocument, index: number, active: number, zoom: number ): { x: number; y: number } {
  const to = pageOffset( doc, index, zoom );
  const from = pageOffset( doc, active, zoom );

  return { x: to.x - from.x, y: to.y - from.y };
}
